import { z } from 'zod'

// ------ Agenda  -------------------------------------------------

export const AgendaSchema = z.object({
  id: z.number(),
  deal_id: z.number(),
  agenda_column_positions: z.array(z.number()),
  number_styles: z.array(z.string()),
  number_resets: z.array(z.number().nullable()),
  always_nest_numbers: z.boolean(),
})

// ----- Default Columns ------------------------------------------
export const TitleCellDataSchema = z.object({
  model_ids: z.array(z.number()).nullable(),
  value: z.string().nullable(),
})

export const VersionCellDataSchema = z.object({
  model_ids: z.array(z.number()).nullable(),
  value: z
    .object({
      dms: z
        .object({
          doc_id: z.string().nullable(),
          status: z.string().nullable(),
          current_version: z.number().nullable(),
        })
        .nullable(),
      closing_version: z
        .object({
          state: z.string(),
          ctype: z.string(),
        })
        .nullable(),
      latest_version: z
        .object({
          is_final: z.boolean(),
          firm: z.string().nullable(),
          date_created: z.string(),
          number: z.number().nullable(),
        })
        .nullable(),
    })
    .nullable(),
})

export const AccessCellDataSchema = z.object({
  model_ids: z.array(z.number()).nullable(),
  value: z.object({
    deal_owner: z.string(),
    external_firms: z.array(z.string()).nullable(),
    is_public: z.boolean(),
  }),
})

export const ActionItemCellDataSchema = z.object({
  model_ids: z.array(z.number()).nullable(),
  value: z
    .object({
      colour: z.number().nullable(),
      title: z.string().nullable(),
    })
    .nullable(),
})

export const NoteCellDataSchema = z.object({
  model_ids: z.array(z.number()).nullable(),
  value: z
    .object({
      unresolved_notes_count: z.number(),
      latest_note: z.object({
        body: z.string(),
        created_by: z.string(),
        created_by_id: z.number(),
        date_created: z.string(),
        last_updated: z.string(),
      }),
    })
    .nullable(),
})

export const SignatureCellDataSchema = z.object({
  model_ids: z.array(z.number()).nullable(),
  value: z
    .object({
      fraction: z.string().nullable(),
      state: z.string().nullable(),
      needs_review: z.boolean(),
    })
    .nullable(),
})

export const ScheduleCellDataSchema = z.object({
  model_ids: z.array(z.number()).nullable(),
  value: z
    .object({
      state: z.string().nullable(),
      fraction: z.string().nullable(),
    })
    .nullable(),
})

export const ResponsibilityCellDataSchema = z.object({
  model_ids: z.array(z.number()).nullable(),
  value: z.array(z.string()).nullable(),
})

// ----- Custom Columns -------------------------------------------------

export const CustomEntryCellDataSchema = z.object({
  model_ids: z.array(z.number()).nullable(),
  value: z.string().nullable(),
})

export const DateCellDataSchema = CustomEntryCellDataSchema
export const TextCellDataSchema = CustomEntryCellDataSchema
export const NumberCellDataSchema = CustomEntryCellDataSchema
export const DropdownCellDataSchema = CustomEntryCellDataSchema
export const StatusDropdownCellDataSchema = CustomEntryCellDataSchema

export const AgendaObjectCBSchema = z.object({
  id: z.number(),
  content_id: z.number(),
  content_type: z.enum(['category', 'item', 'action-item']),
  gas_is_redacted: z.boolean(),
  number_level: z.number(),
  position: z.number(),
  column_data: z.record(
    z.string(),
    z.object({
      value: z
        .union([
          z.string(),
          // Version Column
          z.object({
            dms: z.unknown().nullable(),
            closing_version: z
              .object({
                state: z.string(),
                ctype: z.string(),
              })
              .nullable(),
            latest_version: z
              .object({
                is_final: z.boolean(),
                firm: z.string().nullable(),
                date_created: z.string(),
                number: z.number().nullable(),
              })
              .nullable(),
          }),
          // Action Item Column
          z.object({
            colour: z.number().nullable(),
            title: z.string().nullable(),
          }),
          // Notes Column
          z.object({
            unresolved_notes_count: z.number(),
            latest_note: z.object({
              body: z.string(),
              created_by: z.string(),
              date_created: z.string(),
              last_updated: z.string(),
              created_by_id: z.number(),
            }),
          }),
          // Access Control Column
          z.object({
            deal_owner: z.string(),
            external_firms: z.array(z.string()).nullable(),
            is_public: z.boolean(),
          }),
          // Signature Column
          z.object({
            fraction: z.string().nullable(),
            state: z.string().nullable(),
            needs_review: z.boolean(),
          }),
          // Schedule Column
          z.object({
            state: z.string().nullable(),
            fraction: z.string().nullable(),
          }),
          // Responsibility Column
          z.array(z.string()),
        ])
        .nullable(),
      model_ids: z.array(z.number()).nullable().optional(),
    }),
  ),
  number: z.string().nullable().optional(),
  hasAccess: z.boolean().optional(),
  closingBookDisabled: z.boolean().optional(),
  documentExtension: z.string().nullable().optional(),
})

export const AgendaObjectServerSchema = z.object({
  id: z.number(),
  content_id: z.number(),
  content_type: z.enum(['category', 'item', 'action-item']),
  gas_is_redacted: z.boolean(),
  number_level: z.number(),
  position: z.number(),
  column_data: z.record(
    z.string(),
    z.union([
      TitleCellDataSchema,
      ResponsibilityCellDataSchema,
      VersionCellDataSchema,
      ActionItemCellDataSchema,
      SignatureCellDataSchema,
      ScheduleCellDataSchema,
      NoteCellDataSchema,
      AccessCellDataSchema,
      TextCellDataSchema,
      NumberCellDataSchema,
      DateCellDataSchema,
    ]),
  ),
})

export const AgendaColumnSchema = z.object({
  id: z.number(),
  is_visible: z.boolean(),
  column_type: z.enum([
    'title',
    'responsibility',
    'schedule',
    'signature',
    'version',
    'note',
    'access',
    'text',
    'date',
    'number',
    'dropdown',
    'status-dropdown',
  ]),
  width: z.number(),
  column_title: z.string(),
  options: z
    .array(
      z.object({
        id: z.number(),
        label: z.string(),
        color: z.number(),
        status_category: z.enum(['open', 'in-progress', 'closed']).nullable().optional(),
      }),
    )
    .optional(),
})

export const AgendaColumnEntrySchema = z.object({
  agenda_column_id: z.number(),
  agenda_object_id: z.number(),
  id: z.number(),
  value: z.string(),
})
